/**
 * TeachingTabContent Component
 * Content for the Teaching tab in the profile detail view
 * Shows ritual cards the user is teaching with search, filters, and grid layout
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { RitualCard } from "@/components/cards/ritualCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { formatRitualType } from "@/features/rituals/types"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { useToggleRitualSave } from "../hooks/useSavedRituals"
import { useUserRitualTeachings } from "../hooks/useUserRitualTeachings"

interface TeachingTabContentProps {
  userId: string
  totalCount?: number
  className?: string
  searchQuery?: string
}

export function TeachingTabContent({ userId, className, searchQuery }: TeachingTabContentProps) {
  const { t } = useTranslation("profile")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)

  // Toggle save hook
  const toggleRitualSave = useToggleRitualSave()

  const { data, isLoading, isError } = useUserRitualTeachings(userId, {
    limit: 50,
    search: debouncedSearch || undefined,
  })

  // Transform API data to match RitualCardProps
  const rituals =
    data?.rituals.map(ritual => ({
      id: ritual.id,
      image: ritual.uploads?.find(u => u.isCover)?.filePath || undefined,
      userAvatar: ritual.createdBy?.coverImage || undefined,
      username: ritual.createdBy?.name || t("teaching.unknown"),
      userHandle: ritual.createdBy?.username ? `@${ritual.createdBy.username}` : "",
      title: ritual.title,
      tags: [ritual.participationFee, formatRitualType(ritual.type)],
      duration: `${ritual.duration} min`,
      contextualTag: formatRitualType(ritual.type),
      userId: ritual.createdBy?.id,
      isBookmarked: ritual.isSaved,
      onBookmarkClick: () => toggleRitualSave.mutate(ritual.id),
    })) || []

  return (
    <div className={className}>
      {/* Loading State */}
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">{t("teaching.loading")}</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">{t("teaching.error")}</p>
        </div>
      )}

      {/* Rituals Grid */}
      {!isLoading && !isError && (
        <div className="mt-0 px-5">
          <div className="flex justify-between items-center gap-2">
            <h1 className="text-[18px] leading-[30px] font-monteserat font-semibold uppercase">
              {t("teaching.title")}
            </h1>
            <div className="lg:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label="Close search"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => setShowSearch(true)}
                  aria-label="Search"
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>
          <p className="mt-2.5 md:mt-3 text-[14px] font-regular leading-[25px]">
            {t("teaching.description")}
          </p>
          {rituals.length === 0 ? (
            <div className="h-[250px] mt-4">
              <p className="text-stone mt-3 text-[14px] font-regular leading-[25px]">
                {t("teaching.notFound")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-5 md:my-10">
              {rituals.map(ritual => (
                <div key={ritual.id} className="h-[260px] md:h-[440px]">
                  <RitualCard {...ritual} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
